import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import type { BusinessAISettings } from '../types';

const DEFAULTS: Omit<BusinessAISettings, 'business_id'> = {
  ai_enabled: false, description_cleanup: true, message_suggestions: true,
  quote_scope_writer: true, receipt_scanning: true, expense_categorization: true,
  photo_estimates: false, photo_reports: false, change_order_analysis: false,
  daily_summary: false,
};

export interface AIUsageSummary {
  totalCalls: number;
  estimatedCost: number;
  byFeature: { feature: string; count: number }[];
}

export function useAISettings(businessId: string | undefined) {
  const [settings, setSettings] = useState<BusinessAISettings | null>(null);
  const [usage, setUsage] = useState<AIUsageSummary>({ totalCalls: 0, estimatedCost: 0, byFeature: [] });
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const fetch = useCallback(async () => {
    if (!businessId) { setLoading(false); return; }
    setLoading(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 10000);
    try {
      let { data: ai } = await supabase.from('business_ai_settings').select('*')
        .eq('business_id', businessId).maybeSingle();
      if (!ai) {
        const { data: created } = await supabase.from('business_ai_settings')
          .insert({ business_id: businessId, ...DEFAULTS })
          .select('*').maybeSingle();
        ai = created;
      }
      // Usage this month
      const monthStart = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01T00:00:00`;
      const { data: logs } = await supabase.from('ai_usage_log').select('feature, cost')
        .eq('business_id', businessId).gte('created_at', monthStart).abortSignal(ctrl.signal);
      const featureMap = new Map<string, number>();
      let totalCost = 0;
      for (const log of logs ?? []) {
        featureMap.set(log.feature, (featureMap.get(log.feature) ?? 0) + 1);
        totalCost += (log.cost ?? 0);
      }
      const byFeature = Array.from(featureMap.entries()).map(([feature, count]) => ({ feature, count }))
        .sort((a, b) => b.count - a.count);

      if (mountedRef.current) {
        setSettings(ai as BusinessAISettings);
        setUsage({ totalCalls: (logs ?? []).length, estimatedCost: totalCost, byFeature });
      }
    } catch { /* */ }
    finally { clearTimeout(t); if (mountedRef.current) setLoading(false); }
  }, [businessId]);

  useEffect(() => { mountedRef.current = true; fetch(); return () => { mountedRef.current = false; }; }, [fetch]);

  const updateToggle = useCallback(async (field: string, value: boolean, userId: string): Promise<boolean> => {
    if (!businessId || !settings) return false;
    try {
      const { error: e } = await supabase.from('business_ai_settings')
        .update({ [field]: value }).eq('business_id', businessId);
      if (e) return false;
      try { await supabase.from('activity_log').insert({
        business_id: businessId, activity_type: 'ai_setting_changed',
        description: `AI setting ${field} → ${value}`, entity_type: 'business',
        entity_id: businessId, logged_by: userId,
      }); } catch { /* */ }
      fetch(); return true;
    } catch { return false; }
  }, [businessId, settings, fetch]);

  return { settings, usage, loading, updateToggle, refresh: fetch };
}
